import {
  ArtefactFilterCandidate,
  LiquidityWindow,
  getCandidateProfit,
  getDailySalesRate
} from './artefact-filters'

interface ScoreCandidate extends ArtefactFilterCandidate {
  currentPrice?: number | string | null
  currentBid?: number | string | null
  startPrice?: number | string | null
  isBid?: boolean
  lotsCount?: number
}

export interface ArtefactScoreDetails {
  score: number
  profit: number
  roiPercent: number
  dailySalesRate: number
  historyCount: number
  points: { roi: number; profit: number; liquidity: number; history: number }
}

// Fixed reference values keep a candidate's score independent of other visible rows.
// Each factor reaches half its weight at its reference value.
const SCORE_FACTORS = {
  roi: { weight: 35, reference: 20 },
  profit: { weight: 25, reference: 100_000 },
  liquidity: { weight: 25, reference: 10 },
  history: { weight: 15, reference: 50 }
} as const

function positiveNumber(value: unknown): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
}

function factorPoints(value: number, factor: { weight: number; reference: number }): number {
  return factor.weight * (value / (value + factor.reference))
}

export function calculateArtefactScore(
  candidate: ScoreCandidate,
  window: LiquidityWindow = 48,
  now = Date.now()
): ArtefactScoreDetails {
  const profit = getCandidateProfit(candidate)
  const cost = positiveNumber(
    candidate.isBid
      ? candidate.currentBid ?? candidate.startPrice ?? candidate.currentPrice
      : candidate.currentPrice
  )
  const historyCount = positiveNumber(candidate.historyCount ?? candidate.historySalePrices?.length)
  const dailySalesRate =
    positiveNumber(getDailySalesRate(candidate, window, now)) /
    Math.max(1, positiveNumber(candidate.lotsCount))
  const roiPercent = cost > 0 ? (profit / cost) * 100 : 0
  const points = { roi: 0, profit: 0, liquidity: 0, history: 0 }

  if (cost > 0 && profit > 0 && historyCount > 0 && Number.isFinite(roiPercent)) {
    points.roi = factorPoints(roiPercent, SCORE_FACTORS.roi)
    points.profit = factorPoints(profit, SCORE_FACTORS.profit)
    points.liquidity = factorPoints(dailySalesRate, SCORE_FACTORS.liquidity)
    points.history = factorPoints(historyCount, SCORE_FACTORS.history)
  }

  return {
    score: points.roi + points.profit + points.liquidity + points.history,
    profit,
    roiPercent: Number.isFinite(roiPercent) ? roiPercent : 0,
    dailySalesRate,
    historyCount,
    points
  }
}

export function artefactScoreTooltip(details: ArtefactScoreDetails): string {
  const format = (value: number) => value.toLocaleString('ru-RU', { maximumFractionDigits: 1 })

  return [
    'Score 0–100',
    `ROI ${format(details.roiPercent)}%: ${format(details.points.roi)}/35`,
    `Профит ${format(details.profit)}: ${format(details.points.profit)}/25`,
    `Продажи/сутки ${format(details.dailySalesRate)}: ${format(details.points.liquidity)}/25`,
    `История ${format(details.historyCount)} продаж: ${format(details.points.history)}/15`,
    'Фактор = вес × значение / (значение + ориентир). Ориентиры: ROI 20%, профит 100 000, продажи/сутки 10, история 50.'
  ].join('; ')
}
